import { Box } from "@mui/material"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { Row } from "@tanstack/react-table"
import { type ChangeEvent, type FC, useState } from "react"

import { ALL_WAREHOUSES_VALUE } from "../../constants"
import { useProductInventoryFilterOptions, useProductInventoryList } from "../../hooks"

import { ProductInventoryFilterChips } from "./ProductInventoryFilterChips"
import { ProductInventoryFilterDrawer } from "./ProductInventoryFilterDrawer"
import { ProductInventoryTable } from "./ProductInventoryTable"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import { AerosSelect } from "@/components/UiKit"
import { PermissionActionType } from "@/constants/permissions"
import type { InventoryByProductItem } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export const ProductInventoryListPage: FC = () => {
  const navigate = useNavigate()
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.INVENTORY_MAINTENANCE])

  const {
    inventories,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    handleScroll,
  } = useProductInventoryList()

  const filterOptions = useProductInventoryFilterOptions()

  const handleFilterDrawerToggle = () => {
    setIsFilterDrawerOpen((prev) => !prev)
  }

  const handleRowClick = (row: Row<InventoryByProductItem>) => {
    navigate({
      to: "/app/inventory/products/$productId",
      params: { productId: row.original.product.productId.toString() },
    })
  }

  const handleAdjust = (_item: InventoryByProductItem) => {
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
  }

  const handleTransfer = (_item: InventoryByProductItem) => {
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
  }

  const handleProductView = (item: InventoryByProductItem) => {
    navigate({
      to: "/app/product/$productId/edit",
      params: { productId: item.product.productId.toString() },
    })
  }

  const handleCoaView = (_item: InventoryByProductItem) => {
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
  }

  return (
    <Box className='flex h-full flex-col gap-y-6'>
      <Box className='flex items-center gap-x-4'>
        <SearchInput
          placeholder='Search by Product ID or Description'
          defaultValue={searchParams.query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
          className='w-full max-w-[524px]'
          data-testid='product-inventory.list-page.search-field'
        />
        <Button
          appearance='outlined'
          startIcon={<FilterAltOutlined />}
          onClick={handleFilterDrawerToggle}
          data-testid='product-inventory.list-page.filter-button'
        >
          Filter
        </Button>
        <AerosSelect
          options={filterOptions.warehouseOptions}
          value={
            filterOptions.isWarehouseOptionsFetching
              ? ALL_WAREHOUSES_VALUE
              : (searchParams.warehouseAreaId ?? ALL_WAREHOUSES_VALUE)
          }
          onChange={(value) =>
            !value || value === ALL_WAREHOUSES_VALUE
              ? handleFilterRemove("warehouseAreaId")
              : handleFilterChange("warehouseAreaId", value)
          }
          className='w-[240px]'
          aria-label='Warehouse'
          data-testid='product-inventory.list-page.warehouse-filter'
        />
      </Box>
      <ProductInventoryFilterChips {...filterOptions} searchParams={searchParams} onRemove={handleFilterRemove} />
      <Box className='flex min-h-0 flex-1 overflow-y-auto'>
        <ProductInventoryTable
          data={inventories}
          loading={isFetching}
          error={error}
          sorting={sorting}
          containerRef={tableContainerRef}
          canMaintain={canMaintain}
          onSortingChange={handleSortingChange}
          onScroll={handleScroll}
          onRowClick={handleRowClick}
          onAdjust={handleAdjust}
          onTransfer={handleTransfer}
          onProductView={handleProductView}
          onCoaView={handleCoaView}
        />
      </Box>
      <ProductInventoryFilterDrawer
        {...filterOptions}
        open={isFilterDrawerOpen}
        searchParams={searchParams}
        onToggle={handleFilterDrawerToggle}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
      />
    </Box>
  )
}
